/**
 * Starter legal copy for Treydora public policies.
 *
 * DEVELOPER / LEGAL: This is template text for engineering and UX integration only.
 * It MUST be reviewed, revised, and approved by qualified legal counsel before any
 * production launch or consumer-facing reliance.
 */

export const LEGAL_SECTIONS_BY_SLUG = {
  terms: [
    {
      id: "acceptance",
      title: "1. Agreement to Terms",
      paragraphs: [
        "By accessing or using Treydora websites, applications, dashboards, checkout experiences, or related services (collectively, the “Services”), you agree to these Terms of Use and our Privacy Policy.",
        "If you do not agree, do not use the Services. Treydora may update these Terms; continued use after notice constitutes acceptance of the revised Terms where permitted by law.",
      ],
    },
    {
      id: "eligibility-accounts",
      title: "2. Eligibility & Accounts",
      paragraphs: [
        "You must provide accurate registration information and safeguard your credentials. You are responsible for activity under your account except where Treydora’s systems are compromised through no fault of yours.",
        "Treydora may suspend or terminate accounts that violate these Terms, create security risks, or abuse support channels.",
      ],
    },
    {
      id: "digital-services",
      title: "3. Digital Services & Access",
      paragraphs: [
        "Treydora may offer digital readers, libraries, dashboards, VR experiences, and blockchain-linked features. Access may require compatible devices, wallets, and network connectivity.",
        "Treydora does not guarantee uninterrupted availability, error-free operation, or preservation of any particular feature set. Digital experiences may change for compatibility, security, or creative direction.",
      ],
    },
    {
      id: "nft-blockchain",
      title: "4. Blockchain, NFTs & Third Parties",
      paragraphs: [
        "Certain features interact with public blockchains, smart contracts, and third-party wallets or payment processors. You accept the risks described in the Digital Asset Risk Disclosure.",
        "Nothing in the Services constitutes an offer of securities, investment advice, legal advice, tax advice, or a promise of profit, liquidity, resale value, or market depth.",
      ],
    },
    {
      id: "license-ip",
      title: "5. License to Treydora IP; Restrictions",
      paragraphs: [
        "Treydora and its licensors own the Services, branding, artwork, narrative content, software, and underlying intellectual property, except where expressly licensed (for example, limited merchandiser rights described in the NFT Comic Merchandiser License Terms).",
        "Unless expressly permitted, you may not reverse engineer, scrape in violation of robots rules, circumvent access controls, or use the Services to build a competing product using Treydora proprietary materials.",
      ],
    },
    {
      id: "marketplace-sellers",
      title: "6. Marketplace & Storefront Participation",
      paragraphs: [
        "If you publish or sell through Treydora marketplace tools, additional Marketplace Seller Terms apply, including rules on prohibited goods, fulfillment, chargebacks, and platform transaction fees.",
      ],
    },
    {
      id: "disclaimers",
      title: "7. Disclaimers",
      paragraphs: [
        "EXCEPT WHERE PROHIBITED BY LAW, THE SERVICES ARE PROVIDED “AS IS” AND “AS AVAILABLE,” WITHOUT WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.",
        "Treydora does not warrant that blockchain transactions will confirm promptly, that third-party APIs will remain compatible, or that any digital collectible will retain display metadata permanently.",
      ],
    },
    {
      id: "liability",
      title: "8. Limitation of Liability",
      paragraphs: [
        "TO THE MAXIMUM EXTENT PERMITTED BY LAW, TREYDORA AND ITS AFFILIATES WILL NOT BE LIABLE FOR INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR LOST PROFITS, ARISING OUT OF OR RELATED TO THE SERVICES.",
        "Treydora’s aggregate liability for claims arising out of the Services in any twelve-month period is limited to the greater of (a) the amounts you paid Treydora for the specific transaction giving rise to the claim, or (b) one hundred U.S. dollars (US$100), except where a different minimum applies under mandatory law.",
      ],
    },
    {
      id: "governing-law",
      title: "9. Governing Law; Disputes",
      paragraphs: [
        "These Terms are governed by the laws of the State of Delaware, excluding conflict-of-law rules, unless a different governing law is required for consumers in your jurisdiction.",
        "You and Treydora agree to exclusive jurisdiction and venue in the state or federal courts located in Delaware for disputes not subject to arbitration, except where prohibited.",
      ],
    },
  ],

  privacy: [
    {
      id: "scope",
      title: "1. Scope",
      paragraphs: [
        "This Privacy Policy describes how Treydora Productions, Inc. (“Treydora,” “we,” or “us”) may collect, use, disclose, and safeguard information when you use the Services.",
      ],
    },
    {
      id: "data-we-collect",
      title: "2. Information We May Collect",
      paragraphs: [
        "Account identifiers (such as email, username, and authentication tokens).",
        "Transaction data (such as checkout references, entitlement identifiers, and on-chain transaction hashes you voluntarily submit for sync).",
        "Device and log data (such as IP address, user agent, coarse location derived from IP, and diagnostic events).",
        "Support communications and optional attachments you send to Treydora.",
      ],
    },
    {
      id: "how-we-use",
      title: "3. How We Use Information",
      paragraphs: [
        "To provide, secure, and improve the Services; authenticate users; process purchases; detect fraud and abuse; comply with law; and communicate transactional or service messages.",
        "We do not sell your personal information for money. Where required, we honor applicable opt-out rights for certain sharing that may be deemed a “sale” or “sharing” under state privacy laws.",
      ],
    },
    {
      id: "processors",
      title: "4. Service Providers & Processors",
      paragraphs: [
        "Treydora uses vendors (for example, payment processors, hosting providers, email delivery, analytics, and customer-support tooling) that process data under contractual obligations.",
      ],
    },
    {
      id: "retention",
      title: "5. Retention",
      paragraphs: [
        "We retain information as long as needed to provide the Services, meet legal obligations, resolve disputes, and enforce agreements. Some logs may be retained in aggregated or de-identified form.",
      ],
    },
    {
      id: "security",
      title: "6. Security",
      paragraphs: [
        "Treydora employs reasonable administrative, technical, and organizational safeguards. No method of transmission or storage is completely secure; you use the Services at your own risk.",
      ],
    },
    {
      id: "rights",
      title: "7. Your Rights & Choices",
      paragraphs: [
        "Depending on jurisdiction, you may have rights to access, correct, delete, or export personal data, and to object to certain processing. Contact privacy@treydora.com (or the address published on the site) to exercise rights.",
      ],
    },
    {
      id: "children",
      title: "8. Children",
      paragraphs: [
        "The Services are not directed to children under 13 (or the age required in your jurisdiction). Treydora does not knowingly collect personal information from children in violation of law.",
      ],
    },
    {
      id: "changes-contact",
      title: "9. Changes; Contact",
      paragraphs: [
        "We may update this Policy and will post the revised version with an updated date. For questions, contact info@treydora.com.",
      ],
    },
  ],

  "digital-asset-risk": [
    {
      id: "purpose",
      title: "1. Purpose",
      paragraphs: [
        "This Digital Asset Risk Disclosure highlights general categories of risk associated with blockchain-linked products, wallets, and digital access sold through Treydora. It is not exhaustive.",
      ],
    },
    {
      id: "no-advice",
      title: "2. No Financial, Legal, or Tax Advice",
      paragraphs: [
        "Treydora does not provide investment advice, legal advice, or tax advice. You should consult independent professionals regarding digital assets, reporting obligations, and regulatory treatment in your jurisdiction.",
      ],
    },
    {
      id: "market-volatility",
      title: "3. Price Volatility & Liquidity",
      paragraphs: [
        "Digital assets can be extremely volatile. Past pricing or availability does not predict future results. There may be limited liquidity, wide spreads, or periods without willing buyers or sellers.",
      ],
    },
    {
      id: "wallet-custody",
      title: "4. Wallet, Custody & Key Management",
      paragraphs: [
        "If you use self-custody wallets, loss of seed phrases or private keys can cause permanent loss of assets. Malware, phishing, and social engineering may compromise wallets independent of Treydora systems.",
      ],
    },
    {
      id: "smart-contracts",
      title: "5. Smart Contract, Bridge & Network Risk",
      paragraphs: [
        "Smart contracts may contain defects, be exploited, be paused, or behave unexpectedly. Blockchain networks may experience congestion, reorganizations, or breaking upgrades. Bridges and wrapped assets carry additional counterparty and implementation risk.",
      ],
    },
    {
      id: "gas-fees",
      title: "6. Gas Fees & Failed Transactions",
      paragraphs: [
        "Users may pay network fees (“gas”) even when transactions fail or revert. Fee markets can spike unpredictably.",
      ],
    },
    {
      id: "payments",
      title: "7. Payment Processor & Account Risk",
      paragraphs: [
        "Card payments depend on third-party processors. Accounts may be suspended for fraud prevention, chargebacks, or policy violations, which can affect access to entitlements while disputes are resolved.",
      ],
    },
    {
      id: "access-changes",
      title: "8. Digital Access Modification Risk",
      paragraphs: [
        "Treydora may update readers, entitlement rules, or platform utility to address law, security, or product design. Digital access is not a guaranteed perpetual feature bundle.",
      ],
    },
  ],

  "nft-comic-merch-license": [
    {
      id: "nature",
      title: "1. Nature of the License",
      paragraphs: [
        "Certain Genesis Comic NFT tiers may include a limited, non-exclusive, revocable merchandiser license when expressly stated at purchase and confirmed in your entitlement records.",
        "Treydora and its licensors retain all right, title, and interest in and to the underlying comic intellectual property, including characters, logos, story text, cover art (excluding separately owned third-party components, if any), and derivative marketing assets.",
      ],
    },
    {
      id: "condition-ownership",
      title: "2. Conditionality & Ownership",
      paragraphs: [
        "Merchandiser rights exist only while you validly hold the applicable NFT in the designated configuration (for example, not flagged as stolen, subject to a valid marketplace escrow, or revoked for breach).",
        "Transfer of the NFT may cause merchandiser rights to transfer according to on-chain ownership, unless terminated for breach or non-payment of required royalties or platform fees described at purchase.",
      ],
    },
    {
      id: "permitted-use",
      title: "3. Permitted Use & Approvals",
      paragraphs: [
        "Where eligible, you may produce a defined category of physical or digital merchandise that complies with Treydora brand guidelines, quality standards, and any pre-approval workflow published by Treydora.",
        "Treydora may require samples, disclaimers on packaging, and geographic restrictions to protect brand integrity and legal compliance.",
      ],
    },
    {
      id: "prohibited",
      title: "4. Prohibited Uses",
      paragraphs: [
        "No use that implies endorsement by unrelated brands, hate content, illegal products, unsafe goods, sexually explicit depictions of minors, or infringement of third-party rights.",
        "No securities offerings, investment products, or financial instruments marketed on the basis of the NFT’s expected appreciation.",
      ],
    },
    {
      id: "revocation",
      title: "5. Suspension & Revocation",
      paragraphs: [
        "Treydora may suspend or revoke merchandiser rights for breach, fraud, counterfeit goods, or legal demands, without refund beyond what the Refund Policy separately requires.",
      ],
    },
    {
      id: "disclaimer",
      title: "6. Disclaimer",
      paragraphs: [
        "THE MERCHANDISER LICENSE IS PROVIDED “AS IS.” TREYDORA DISCLAIMS WARRANTIES TO THE MAXIMUM EXTENT PERMITTED BY LAW AND DOES NOT GUARANTEE SALES VOLUME, RETAIL DISTRIBUTION, OR MARKETPLACE LISTING AVAILABILITY.",
      ],
    },
  ],

  "vr-real-estate-terms": [
    {
      id: "virtual-only",
      title: "1. Virtual Real Estate & Digital Locations Only",
      paragraphs: [
        "VR Real Estate NFTs and related purchases represent digital licenses, cosmetic status, or storefront access inside Treydora / Oshar experiences as described at the point of sale.",
        "These products do not convey ownership of real-world land, mineral rights, air rights, water rights, or physical addresses. They are not deeds, title insurance, or government registry entries.",
      ],
    },
    {
      id: "no-investment",
      title: "2. No Securities, Equity, or Guaranteed Economics",
      paragraphs: [
        "Purchases are not shares, equity interests, profit interests, loans, or collective investment schemes. Treydora does not promise passive income, rent, royalties, appreciation, liquidity, or resale value.",
      ],
    },
    {
      id: "utility",
      title: "3. Platform Utility & Changes",
      paragraphs: [
        "Utility may include access to themed environments, commerce slots, lore integrations, or dashboard badges as described in product materials. Features may evolve, rotate, or sunset for technical, creative, or legal reasons.",
      ],
    },
    {
      id: "stripe-wallet",
      title: "4. Payment Rails",
      paragraphs: [
        "Some tiers may be purchased with card payments processed by third parties; others may involve on-chain transactions with separate network fees and risks. See the Digital Asset Risk Disclosure.",
      ],
    },
    {
      id: "compliance",
      title: "5. Compliance & Sanctions",
      paragraphs: [
        "You may not use VR real estate products to facilitate illegal activity, sanctions evasion, or unlicensed financial services. Treydora may block access where required by law.",
      ],
    },
  ],

  "marketplace-seller-terms": [
    {
      id: "seller-relationship",
      title: "1. Seller Relationship",
      paragraphs: [
        "When marketplace seller tools are available, sellers operate as independent merchants. Treydora provides software and payment facilitation features subject to these Terms and any seller onboarding agreements.",
      ],
    },
    {
      id: "listings",
      title: "2. Listings, Accuracy & IP",
      paragraphs: [
        "Sellers must accurately describe goods, delivery method, and restrictions. Sellers warrant they have rights to sell listed content and that listings comply with the Content Standards.",
      ],
    },
    {
      id: "fees",
      title: "3. Platform Transaction Fees & Payouts",
      paragraphs: [
        "Treydora may charge a platform transaction fee on qualifying sales. Fees compensate for payment processing, fraud monitoring, hosting, and support — they are not taxes unless separately itemized as tax collected by Treydora where required.",
        "Payout timing may depend on processor schedules, chargeback windows, and identity verification.",
      ],
    },
    {
      id: "prohibited-goods",
      title: "4. Prohibited & Restricted Goods",
      paragraphs: [
        "Sellers may not list illegal goods, regulated items without required licenses, malware, stolen credentials, or NFTs marketed as securities or investment contracts.",
      ],
    },
    {
      id: "enforcement",
      title: "5. Enforcement",
      paragraphs: [
        "Treydora may delist products, withhold payouts pending investigation, and terminate seller access for repeated violations or legal risk.",
      ],
    },
  ],

  "refund-policy": [
    {
      id: "overview",
      title: "1. Overview",
      paragraphs: [
        "This Refund Policy explains how Treydora approaches refunds and credits for digital goods purchased through Treydora checkout experiences. It is a general template and may be superseded by specific terms shown at checkout.",
      ],
    },
    {
      id: "digital-delivery",
      title: "2. Digital Delivery",
      paragraphs: [
        "Many Treydora products are delivered digitally. Once access is granted to your account or wallet sync completes, Treydora may treat the sale as fulfilled except where law requires a different remedy.",
      ],
    },
    {
      id: "duplicate-billing",
      title: "3. Duplicate Billing & Technical Errors",
      paragraphs: [
        "If you believe you were charged multiple times for the same entitlement, contact support with receipts. Confirmed duplicate charges may be refunded or credited.",
      ],
    },
    {
      id: "chargebacks",
      title: "4. Chargebacks",
      paragraphs: [
        "Initiating a chargeback may result in suspension of entitlements pending investigation. Fraudulent chargebacks may lead to account closure.",
      ],
    },
    {
      id: "contact",
      title: "5. How to Request Help",
      paragraphs: [
        "Email info@treydora.com with your account email, order reference, and a description of the issue. Response times may vary.",
      ],
    },
  ],

  "content-standards": [
    {
      id: "goals",
      title: "1. Goals",
      paragraphs: [
        "Treydora aims to keep community spaces, marketplace listings, and support interactions respectful, lawful, and aligned with the cinematic brand experience.",
      ],
    },
    {
      id: "harassment-hate",
      title: "2. Harassment, Hate & Threats",
      paragraphs: [
        "Do not harass, dox, threaten violence, or promote hate against protected classes. Slurs, glorification of violent extremism, and coordinated brigading are prohibited.",
      ],
    },
    {
      id: "illegal-content",
      title: "3. Illegal & Harmful Content",
      paragraphs: [
        "No child sexual abuse material, no facilitation of human trafficking, no instructions for serious self-harm, and no malware or credential harvesting.",
      ],
    },
    {
      id: "ip-spam",
      title: "4. Intellectual Property & Spam",
      paragraphs: [
        "Respect copyrights and trademarks. Do not spam, astroturf reviews, or manipulate discovery systems.",
      ],
    },
    {
      id: "enforcement-appeals",
      title: "5. Enforcement & Appeals",
      paragraphs: [
        "Treydora may remove content, restrict features, or terminate accounts for violations. Appeals may be offered where feasible and lawful.",
      ],
    },
  ],
};

export function getLegalSections(slug) {
  const key = String(slug || "").trim().toLowerCase();
  return LEGAL_SECTIONS_BY_SLUG[key] || [];
}
